import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowLeft, Database, Layers, TrendingUp } from 'lucide-react';

import { ChartCard } from '../components/ChartCard';
import { PageHeader } from '../components/PageHeader';
import { cn } from '../lib/utils';
import { useActivityDashboardMetrics } from '../hooks/useActivityDashboardMetrics';
import { headcountByDept, operationalMetrics, regionPerformance, revenueTrend } from '../lib/mock-data';

const periods = ['MTD', 'QTD', 'YTD', 'FY'];

const fmtCompact = (value) =>
  new Intl.NumberFormat('en-IN', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);

export function ActivityReportPage() {
  const [period, setPeriod] = useState('QTD');
  const { metrics, loading } = useActivityDashboardMetrics(period);

  const revenueData = metrics?.revenueTrajectory?.length ? metrics.revenueTrajectory : revenueTrend;
  const regionData = metrics?.regionPerformance?.length ? metrics.regionPerformance : regionPerformance;
  const opsData = metrics?.operationalMetrics?.length ? metrics.operationalMetrics : operationalMetrics;
  const deptData = metrics?.headcountByDept?.length ? metrics.headcountByDept : headcountByDept;

  const summary = useMemo(() => {
    const revenue = revenueData.reduce((sum, item) => sum + (Number(item.revenue) || 0), 0);
    const activityCount = deptData.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    const topCategory = regionData[0]?.region || 'Not available';
    const averageUptime = opsData.length
      ? opsData.reduce((sum, item) => sum + (Number(item.uptime) || 0), 0) / opsData.length
      : 0;

    return { revenue, activityCount, topCategory, averageUptime };
  }, [deptData, opsData, regionData, revenueData]);

  return (
    <div className="pb-12">
      <PageHeader
        eyebrow="Activity Report"
        title="Activity Performance Report"
        description="Report generated from the activity table with period-based MTD, QTD, YTD and FY views."
      />

      <section className="px-6 lg:px-10 mt-3 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/imagei/reports"
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            All reports
          </Link>

          <div className="flex items-center gap-3">
            {loading && <span className="text-xs text-muted-foreground">Loading activity data...</span>}
            <div className="flex bg-muted rounded-lg p-0.5 gap-0.5">
              {periods.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setPeriod(item)}
                  className={cn(
                    'px-4 py-2 rounded-md text-xs font-semibold transition-all',
                    period === item
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryTile icon={TrendingUp} label="Activity Value" value={fmtCompact(summary.revenue)} />
          <SummaryTile icon={Layers} label="Activity Rows" value={fmtCompact(summary.activityCount)} />
          <SummaryTile icon={Database} label="Top Category" value={summary.topCategory} />
          <SummaryTile icon={TrendingUp} label="Weekly Index" value={`${summary.averageUptime.toFixed(2)}%`} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <ChartCard title="Revenue Trajectory" subtitle="Activity value by business month" className="h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="activityRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.78 0.18 195)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="oklch(0.78 0.18 195)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
                <Tooltip formatter={(value) => fmtCompact(value)} contentStyle={{ background: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="oklch(0.78 0.18 195)" strokeWidth={2.5} fill="url(#activityRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Category Performance" subtitle="Actual vs index target" className="h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={regionData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="region" stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'var(--color-border)' }} />
                <Bar dataKey="target" fill="var(--color-border)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" fill="oklch(0.78 0.18 195)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Weekly Activity Index" subtitle="Last 6 activity weeks" className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={opsData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="week" stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} domain={[99.8, 100]} />
                <Tooltip contentStyle={{ background: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="uptime" stroke="oklch(0.74 0.18 155)" strokeWidth={2.5} dot={{ fill: 'oklch(0.74 0.18 155)', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Activity Rows by Section" subtitle="Count from activity table" className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deptData} layout="vertical" margin={{ top: 10, right: 20, left: 30, bottom: 0 }}>
                <CartesianGrid stroke="var(--color-border)" horizontal={false} />
                <XAxis type="number" stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="dept" stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} width={120} />
                <Tooltip contentStyle={{ background: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'var(--color-border)' }} />
                <Bar dataKey="count" fill="oklch(0.7 0.21 305)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </section>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value }) {
  return (
    <div className="surface-card p-4">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="mt-1 truncate text-lg font-semibold text-foreground">{value}</p>
        </div>
      </div>
    </div>
  );
}
